import React, { useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn, Grid3X3 } from "lucide-react";

const toProxy = (url) => {
  if (!url) return url;
  if (url.startsWith("/api/media/proxy")) return url;
  const needsProxy =
    url.includes("drive.google.com") ||
    url.includes("customer-assets.emergentagent.com");

  if (needsProxy) {
    return `/api/media/proxy?url=${encodeURIComponent(url)}`;
  }

  return url;
};

const GalleryBlock = ({ images, alt = "Gallery image", initialMode = "cover", title = "Premium Gallery" }) => {
  const safeImages = useMemo(() => (images || []).filter(Boolean), [images]);
  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState(initialMode);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  const main = toProxy(safeImages[index] || safeImages[0]);
  const totalImages = safeImages.length;

  // Navigation functions
  const goNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % totalImages);
  }, [totalImages]);

  const goPrev = useCallback(() => {
    setIndex((prev) => (prev - 1 + totalImages) % totalImages);
  }, [totalImages]);

  // Keyboard navigation for lightbox
  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowRight") goNext();
    if (e.key === "ArrowLeft") goPrev();
    if (e.key === "Escape") setLightboxOpen(false);
  }, [goNext, goPrev]);

  // Show only first 8 thumbnails initially, with "view all" option
  const visibleThumbnails = showGrid ? safeImages : safeImages.slice(0, 8);
  const hasMoreImages = safeImages.length > 8 && !showGrid;

  return (
    <>
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-4 md:p-5 backdrop-blur-md shadow-[0_18px_60px_rgba(0,0,0,0.35)]" data-testid="gallery-block">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="text-xs tracking-[0.18em] uppercase text-[#C9A227]">{title}</div>
            <div className="text-xs text-white/40">{index + 1} / {totalImages}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="rounded-full border border-white/10 bg-black/30 p-2 text-white/75 hover:bg-black/40 hover:text-white transition"
              title="View fullscreen"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setMode((m) => (m === "cover" ? "contain" : "cover"))}
              className="rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-xs text-white/75 hover:bg-black/40 transition hidden sm:block"
            >
              {mode === "cover" ? "Full image" : "Fill frame"}
            </button>
          </div>
        </div>

        {/* Main Image with Navigation Arrows */}
        <div className="relative group">
          <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-white/10 bg-black/30">
            {main ? (
              <img
                src={main}
                alt={alt}
                loading="lazy"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                className={
                  "h-full w-full transition-all duration-500 cursor-pointer hover:scale-[1.02] " +
                  (mode === "cover" ? "object-cover" : "object-contain")
                }
                style={{ 
                  objectPosition: "center",
                  filter: "saturate(1.1) contrast(1.05) brightness(1.02)",
                }}
                onClick={() => setLightboxOpen(true)}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-white/55">
                No images available
              </div>
            )}
            
            {/* Navigation Arrows */}
            {totalImages > 1 && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  data-testid="gallery-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  data-testid="gallery-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Image Counter Badge */}
            <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-black/60 border border-white/10 text-xs text-white/80 backdrop-blur-sm">
              {index + 1} of {totalImages}
            </div>
          </div>
        </div>

        {/* Thumbnails Grid */}
        {totalImages > 1 && (
          <div className="mt-4">
            <div className={`grid gap-2 ${showGrid ? 'grid-cols-6 md:grid-cols-8' : 'grid-cols-4 md:grid-cols-8'}`}>
              {visibleThumbnails.map((src, i) => {
                const actualIndex = showGrid ? i : i;
                const active = actualIndex === index;
                return (
                  <button
                    key={src + i}
                    type="button"
                    onClick={() => setIndex(actualIndex)}
                    className={
                      "relative aspect-square overflow-hidden rounded-lg border-2 transition-all duration-200 " +
                      (active
                        ? "border-[#C9A227] ring-2 ring-[#C9A227]/30 scale-105"
                        : "border-white/10 hover:border-white/30 hover:scale-102")
                    }
                  >
                    <img
                      src={toProxy(src)}
                      alt={`${alt} thumbnail ${i + 1}`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      className="h-full w-full object-cover"
                      style={{ objectPosition: "center" }}
                    />
                    {active && (
                      <div className="absolute inset-0 bg-[#C9A227]/10" />
                    )}
                  </button>
                );
              })}
              
              {/* Show More Button */}
              {hasMoreImages && (
                <button
                  onClick={() => setShowGrid(true)}
                  className="aspect-square rounded-lg border-2 border-dashed border-white/20 bg-white/5 flex flex-col items-center justify-center text-white/60 hover:border-[#C9A227]/50 hover:text-[#C9A227] transition-all"
                >
                  <Grid3X3 className="w-5 h-5 mb-1" />
                  <span className="text-xs">+{totalImages - 8}</span>
                </button>
              )}
            </div>
            
            {/* Collapse Grid Button */}
            {showGrid && totalImages > 8 && (
              <button
                onClick={() => setShowGrid(false)}
                className="mt-3 w-full py-2 rounded-lg border border-white/10 bg-white/5 text-xs text-white/60 hover:text-white hover:bg-white/10 transition"
              >
                Show fewer images
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* Close Button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-black/60 border border-white/10 text-sm text-white/80">
            {index + 1} / {totalImages}
          </div>

          {/* Main Lightbox Image */}
          <div 
            className="relative max-w-[90vw] max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={main}
              alt={alt}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>

          {/* Navigation Arrows */}
          {totalImages > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}

          {/* Bottom Thumbnail Strip */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2 rounded-xl bg-black/60 backdrop-blur-sm border border-white/10">
            {safeImages.slice(0, 12).map((src, i) => (
              <button
                key={src + i}
                onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                className={`w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition ${
                  i === index ? 'border-[#C9A227]' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={toProxy(src)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
            {totalImages > 12 && (
              <div className="w-14 h-14 flex-shrink-0 rounded-lg bg-white/10 flex items-center justify-center text-xs text-white/60">
                +{totalImages - 12}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GalleryBlock;
